import React, { useState, useEffect } from "react"
import {
  Modal,
  Form,
  Input,
  DatePicker,
  message,
  Row,
  Col,
  InputNumber,
} from "antd"
import dayjs from "dayjs"
import ProposalService from "src/services/ProposalService"

const ModalInputSim = ({ visible, onClose, onSubmit, id, idStatus }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [showStatus, setShowStatus] = useState({
    show: false,
    value: null,
  })

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  useEffect(() => {
    if (!id) return

    const fetchSim = async () => {
      try {
        const response = await ProposalService.detailSimProposal(id)
        const data = response?.success?.sim_details || []

        const simsFormatted = data.map(item => ({
          id: item.ID || "",
          phone_number: item.phone_number || "",
          serial_number: item.serial_number || "",
          activation_deadline: item.activation_deadline
            ? dayjs(item.activation_deadline)
            : null,
          supplier: item.supplier?.name || "",
          carrier: item.carrier?.name || "",
          package: item.package?.name || "",
          unit_price: Number(item.unit_price) || 0,
          price_before_vat: Number(item.price_before_vat) || 0,
          price_after_vat: Number(item.price_after_vat) || 0,
          vat: Number(item.vat) || 0,
        }))
        form.setFieldsValue({ sims: simsFormatted })
      } catch (error) {
        console.error("Lỗi khi lấy danh sách SIM", error)
        message.error("Không thể lấy danh sách SIM")
      }
    }

    fetchSim()
  }, [id, form])
  useEffect(() => {
    const fetchStatusSim = async id => {
      try {
        const { success } = await ProposalService.postStatusSim({
          request_id: id,
        })

        setShowStatus(prev => ({ ...prev, value: success[0]?.order_status }))

        if (
          [0, 1].includes(success[0]?.order_status) &&
          idStatus.supplierCode === "GGG"
        ) {
          setShowStatus(prev => ({ ...prev, show: true }))
        }
      } catch (error) {
        setShowStatus(prev => ({ ...prev, show: false }))
        message.error("Không thể lấy trạng thái đơn")
      }
    }

    if (idStatus?.request_id) {
      fetchStatusSim(idStatus?.request_id)
    }
  }, [idStatus?.request_id])

  const handleFinish = async values => {
    setLoading(true)
    try {
      const formattedSims = values.sims.map(sim => ({
        id: sim.id,
        unit_price: sim.unit_price || 0,
        phone_number: sim.phone_number,
        serial_number: sim.serial_number,
        activation_deadline: sim.activation_deadline
          ? dayjs(sim.activation_deadline).format("YYYY-MM-DD")
          : null,
          price_before_vat: sim.price_before_vat,
          price_after_vat: sim.price_after_vat,
          vat: sim.vat
      }))
      const totalPrices = formattedSims.reduce(
        (acc, item) => {
          acc.total_before_vat += Number(item.price_before_vat);
          acc.total_after_vat += Number(item.price_after_vat);
          return acc;
        },
        { total_before_vat: 0, total_after_vat: 0 }
      );
      const response = await ProposalService.updateDetailSimProposal({
        ...totalPrices,
        id: id,
        simUpdates: formattedSims,
      })

      if (response?.message?.includes("thành công")) {
        message.success(
          response?.message || "Cập nhật SIM trong phiếu thành công!",
        )
        onSubmit?.(formattedSims)
        form.resetFields()
      } else {
        message.error(response?.message || "Cập nhật SIM thất bại!")
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message || ""

      // Regex cho cả tiếng Việt và tiếng Anh
      const serialMatch = apiMessage.match(
        /(?:Serial number|Số seri) '([\d\w]+)'/i,
      )
      const phoneMatch = apiMessage.match(
        /(?:Phone number|Số điện thoại) '([\d\w]+)'/i,
      )

      const fieldErrors = []

      values.sims.forEach((sim, index) => {
        if (serialMatch && sim.serial_number === serialMatch[1]) {
          fieldErrors.push({
            name: ["sims", index, "serial_number"],
            errors: [`Seri SIM ${serialMatch[1]} đã tồn tại!`],
          })
        }
        if (phoneMatch && sim.phone_number === phoneMatch[1]) {
          fieldErrors.push({
            name: ["sims", index, "phone_number"],
            errors: [`Số điện thoại ${phoneMatch[1]} đã tồn tại!`],
          })
        }
      })

      if (fieldErrors.length > 0) {
        form.setFields(fieldErrors)
        // Tự cuộn đến trường lỗi đầu tiên
        const modalBody = document.querySelector(".ant-modal-body")
        if (modalBody) {
          const firstErrorNode = document.getElementById(
            fieldErrors[0].name.join("_"),
          )
          if (firstErrorNode) {
            modalBody.scrollTo({
              top:
                firstErrorNode.getBoundingClientRect().top -
                modalBody.getBoundingClientRect().top +
                modalBody.scrollTop -
                50, // offset 50px để dễ nhìn hơn
              behavior: "smooth",
            })
          }
        }
      } else {
        message.error(apiMessage || "Có lỗi xảy ra khi cập nhật SIM")
      }

      console.error("Lỗi khi cập nhật SIM", error)
    } finally {
      setLoading(false)
    }
  }

  function getStatusText(status) {
    let statusText = ""
    let statusColor = ""
    switch (status) {
      case 0:
        statusText = "Chờ duyệt"
        statusColor = "orange"
        break
      case 1:
        statusText = "Hoàn thành"
        statusColor = "green"
        break
      default:
        statusText = "Không xác định"
        statusColor = "gray"
        break
    }
    return (
      <span style={{ color: statusColor, fontWeight: "bold" }}>
        {statusText}
      </span>
    )
  }

  const calculateVAT = (price, vat, type = 'before') => {
    const vatRate = Number(vat) / 100;
    if (type === 'after') {
      return Number(price * (1 + vatRate)).toFixed(0);
    } else {
      return Number(price * (1 - vatRate)).toFixed(0);
    }
  };

  return (
    <Modal
      title="Nhập thông tin SIM"
      open={visible}
      onCancel={handleClose}
      width={1400}
      okText="Cập nhật"
      onOk={() => form.submit()} // submit form khi bấm nút
      confirmLoading={loading}
      styles={{
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        scrollToFirstError={{
          behavior: "smooth",
          block: "center",
        }}
        onFinishFailed={({ errorFields }) => {
          if (errorFields.length > 0) {
            const modalBody = document.querySelector(".ant-modal-body")
            if (modalBody) {
              const firstErrorNode = document.querySelector(
                `[id='${errorFields[0].name.join("_")}']`,
              )
              if (firstErrorNode) {
                modalBody.scrollTo({
                  top:
                    firstErrorNode.getBoundingClientRect().top -
                    modalBody.getBoundingClientRect().top +
                    modalBody.scrollTop -
                    50,
                  behavior: "smooth",
                })
              }
            }
          }
        }}
      >
        <Form.List name="sims">
          {fields =>
            fields.map(({ key, name }) => (
              <div
                key={key}
                style={{
                  padding: "12px",
                  marginBottom: "16px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                }}
              >
                <Row gutter={16}>
                  <Form.Item name={[name, "id"]} hidden>
                    <Input type="hidden" />
                  </Form.Item>
                  <Col xs={24} sm={12} md={8} lg={8} xl={3}>
                    <Form.Item name={[name, "supplier"]} label="Nhà cung cấp">
                      <Input placeholder="Nhà cung cấp" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                    <Form.Item name={[name, "carrier"]} label="Nhà mạng">
                      <Input placeholder="Nhà mạng" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={5}>
                    <Form.Item name={[name, "package"]} label="Gói cước">
                      <Input placeholder="Gói cước" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                    <Form.Item
                      name={[name, "phone_number"]}
                      label="Số thuê bao"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập số thuê bao!",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value) return Promise.resolve()
                            const sims = getFieldValue("sims")
                            const count = sims.filter(
                              sim => sim && sim.phone_number === value,
                            ).length
                            if (count > 1) {
                              return Promise.reject(
                                "Số thuê bao này đã được nhập!",
                              )
                            }
                            return Promise.resolve()
                          },
                        }),
                      ]}
                    >
                      <Input placeholder="Nhập số thuê bao" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                    <Form.Item
                      name={[name, "serial_number"]}
                      label="Số seri SIM"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập số seri SIM!",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value) return Promise.resolve()
                            const sims = getFieldValue("sims")
                            const count = sims.filter(
                              sim => sim && sim.serial_number === value,
                            ).length
                            if (count > 1) {
                              return Promise.reject(
                                "Số seri SIM này đã được nhập!",
                              )
                            }
                            return Promise.resolve()
                          },
                        }),
                      ]}
                    >
                      <Input placeholder="Nhập số seri SIM" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={4}>
                    <Form.Item
                      name={[name, "activation_deadline"]}
                      label="Thời gian kích hoạt"
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD-MM-YYYY"
                        disabledDate={current =>
                          current && current < dayjs().startOf("day")
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={3}>
                    <Form.Item name={[name, "price_before_vat"]} label="Giá trước VAT">
                      <InputNumber
                        placeholder="Nhập giá trước VAT"
                        style={{ width: "100%" }}
                        min={0}
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        onChange={(value) => {
                          const sims = form.getFieldValue("sims")
                          const vat = sims[name]?.vat || 0
                          const price_after_vat = calculateVAT(value, vat, "after")
                          form.setFieldValue(["sims", name, "price_after_vat"], price_after_vat)
                        }}
                        parser={value => value.replace(/(,*)/g, "")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={2}>
                    <Form.Item name={[name, "vat"]} label="VAT">
                      <InputNumber
                        placeholder="Nhập VAT"
                        style={{ width: "100%" }}
                        min={0}
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        onChange={(val) => {
                          const sims = form.getFieldValue("sims")
                          const before = sims[name]?.price_before_vat || 0
                          const result = calculateVAT(before, val , "after");
                          form.setFieldValue(["sims", name, "price_after_vat"], result)
                        }}
                        parser={value => value.replace(/(,*)/g, "")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={2}>
                    <Form.Item name={[name, "price_after_vat"]} label="Giá sau VAT">
                      <InputNumber
                        placeholder="Nhập giá sau VAT"
                        style={{ width: "100%" }}
                        min={0}
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        onChange={(val) => {
                          const sims = form.getFieldValue("sims")
                          const vat = sims[name]?.vat || 0
                          const result = calculateVAT(val, vat, 'before');
                          form.setFieldValue(["sims", name, "price_before_vat"], result)
                        }}
                        parser={value => value.replace(/(,*)/g, "")}
                      />
                    </Form.Item>
                  </Col>
                  {[0, 1].includes(showStatus.value) && showStatus && (
                    <Col xs={24} sm={12} md={8} lg={8} xl={3}>
                      <Form.Item name={[name, "statusSim"]} label="Trạng thái">
                        <p>{getStatusText(showStatus.value)}</p>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </div>
            ))
          }
        </Form.List>
      </Form>
    </Modal>
  )
}

export default ModalInputSim
